import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import FilterSection from "./ComponentsAnalysis/FilterSection/FilterSection";
import SearchExport from "./ComponentsAnalysis/SearchExport/SearchExport";
import ResultTable from "./ComponentsAnalysis/ResultTable/ResultTable";
import PaginationControls from "./ComponentsAnalysis/PaginationControls/PaginationControls";
import DocumentationHistory from "./ComponentsAnalysis/DocumentationHistory/DocumentationHistory";
import {
  getAvaliacaoSolicitacoesByAno,
  getAvaliacaoSolicitacoesLegacy,
  getEventosSolicitacaoAvaliacao,
  getHistoricoAcaoQuestionario,
} from "../../../services";
import {
  formatDateTime,
  formatHistoricoDocumentacaoDataHora,
} from "../../../utils/date";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../routes/paths";
import type { AnalysisRow } from "./shared/analysisListTypes";
import {
  onlyDigits,
  rowSituacaoMatchesSelectedFilters,
} from "./shared/analysisListUtils";
import { mapSolicitacoesLegacyToAnalysisRows } from "./V1/mapSolicitacoesLegacyToAnalysisRows";
import { mapSolicitacoesV2ToAnalysisRows } from "./V2/mapSolicitacoesV2ToAnalysisRows";
import { getAnoRequestParam } from "./V2/anoRequestParam";
import {
  AVAILABLE_YEARS_FALLBACK,
  getFallbackRowsByYear,
} from "./V2/analysisListFallback";
import "./Analysis.css";
import "./ComponentsAnalysis/FilterSection/FilterSection.css";
import "./ComponentsAnalysis/ResultTable/ResultTable.css";
import "./ComponentsAnalysis/PaginationControls/PaginationControls.css";

const STORAGE_KEY = "analysis_filters_v1";

const ANALYSIS_FORCE_MOCK = process.env.REACT_APP_ANALYSIS_FORCE_MOCK === "true";

const ALL_SITUATIONS: string[] = [
  "Documentação Pendente",
  "Documentação Enviada",
  "Análise Pendente",
  "Análise em Andamento",
  "Adequação Pendente",
  "Análise Concluída",
  "Inapto",
];

type HistoryEntry = {
  acao: string;
  data: string;
  id?: number;
};

type SortConfig = {
  key: string | null;
  direction: string | null;
};

const Analysis = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AnalysisRow[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025]);
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<HistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const skipNextYearFetchRef = useRef(false);

  const [selectedSituation, setSelectedSituation] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return ALL_SITUATIONS;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.selectedSituation)) {
        return (parsed.selectedSituation as unknown[])
          .filter((s): s is string => typeof s === "string")
          .filter((s) => ALL_SITUATIONS.includes(s));
      }
    } catch {}
    return ALL_SITUATIONS;
  });
  const [searchTerm, setSearchTerm] = useState("");

  const [anoPersistidoInicial] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Number.isFinite(parsed?.selectedYear) ? parsed.selectedYear : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (Number.isFinite(parsed?.selectedYear)) {
        setSelectedYear(parsed.selectedYear);
      }

      if (typeof parsed?.searchTerm === "string") {
        setSearchTerm(parsed.searchTerm);
      }

      if (Number.isFinite(parsed?.currentPage))
        setCurrentPage(parsed.currentPage);
      if (Number.isFinite(parsed?.itemsPerPage))
        setItemsPerPage(parsed.itemsPerPage);

      if (parsed?.sortConfig && typeof parsed.sortConfig === "object") {
        const sc = parsed.sortConfig as { key?: string | null; direction?: string | null };
        setSortConfig({
          key: sc.key ?? null,
          direction: sc.direction ?? null,
        });
      }
    } catch (e) {
      console.warn("⚠️ Não foi possível restaurar filtros:", e);
    }
  }, []);

  useEffect(() => {
    try {
      const payload = {
        selectedSituation,
        selectedYear,
        searchTerm,
        currentPage,
        itemsPerPage,
        sortConfig,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("⚠️ Não foi possível salvar filtros da análise:", e);
    }
  }, [selectedSituation, selectedYear, searchTerm, currentPage, itemsPerPage, sortConfig]);

  const extractAnosModelo = (
    response: { avaliacoesModelo?: { anoReferencia: number }[] } | null | undefined,
  ): number[] => {
    const anos =
      response?.avaliacoesModelo?.map((item) => item.anoReferencia) ?? [];
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  const loadRowsForYear = async (ano: number) => {
    if (ano === 2025) {
      const legacyResponse = await getAvaliacaoSolicitacoesLegacy();
      const anosModelo = extractAnosModelo(legacyResponse);
      if (anosModelo.length > 0) {
        setAvailableYears(anosModelo);
      }
      return mapSolicitacoesLegacyToAnalysisRows(
        legacyResponse?.solicitacoes ?? [],
        2025,
      );
    }

    const response = await getAvaliacaoSolicitacoesByAno(getAnoRequestParam(ano));
    const anosModelo = extractAnosModelo(response);
    if (anosModelo.length > 0) {
      setAvailableYears(anosModelo);
    }
    return mapSolicitacoesV2ToAnalysisRows(response?.solicitacoes ?? [], ano);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const anoPersistido = anoPersistidoInicial;

        if (ANALYSIS_FORCE_MOCK) {
          const anoInicial = anoPersistido ?? 2026;
          setAvailableYears(AVAILABLE_YEARS_FALLBACK);
          skipNextYearFetchRef.current = true;
          setSelectedYear(anoInicial);
          setData(getFallbackRowsByYear(anoInicial));
          return;
        }

        const response = await getAvaliacaoSolicitacoesByAno(null);
        const anoSelecionado = response?.anoReferenciaSelecionado ?? 2026;
        const anosDisponiveis = extractAnosModelo(response);
        const anosParaValidar =
          anosDisponiveis.length > 0 ? anosDisponiveis : [2026, 2025];

        setAvailableYears(anosParaValidar);

        const anoInicial =
          anoPersistido !== null && anosParaValidar.includes(anoPersistido)
            ? anoPersistido
            : anoSelecionado;

        skipNextYearFetchRef.current = true;
        setSelectedYear(anoInicial);
        setData(await loadRowsForYear(anoInicial));
      } catch (error) {
        console.error("❌ Erro ao buscar dados da análise:", error);
        if (ANALYSIS_FORCE_MOCK) {
          setAvailableYears(AVAILABLE_YEARS_FALLBACK);
          setData(getFallbackRowsByYear(selectedYear || 2026));
        } else {
          setData([]);
        }
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (initialLoading || !selectedYear) return;
    if (skipNextYearFetchRef.current) {
      skipNextYearFetchRef.current = false;
      return;
    }

    const fetchDataByYear = async () => {
      setLoading(true);
      try {
        if (ANALYSIS_FORCE_MOCK) {
          setData(getFallbackRowsByYear(selectedYear));
          return;
        }

        setData(await loadRowsForYear(selectedYear));
      } catch (error) {
        console.error("❌ Erro ao buscar análise por ano:", error);
        setData(
          ANALYSIS_FORCE_MOCK ? getFallbackRowsByYear(selectedYear) : [],
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDataByYear();
  }, [selectedYear, initialLoading]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedSituation, itemsPerPage, searchTerm]);

  const filteredData = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    const termoDigits = onlyDigits(searchTerm);

    return data.filter((item) => {
      const matchesYear = Number(item.ano) === Number(selectedYear);
      const isSituationMatch = rowSituacaoMatchesSelectedFilters(
        item.situacao,
        selectedSituation,
        ALL_SITUATIONS,
      );

      let matchesSearch = true;
      if (termo || termoDigits) {
        const siglaOk = termo
          ? (item.sigla || "").toLowerCase().includes(termo)
          : false;
        const cnpjOk = termoDigits
          ? onlyDigits(item.cnpj).includes(termoDigits)
          : false;
        matchesSearch = siglaOk || cnpjOk;
      }

      return matchesYear && isSituationMatch && matchesSearch;
    });
  }, [data, selectedYear, selectedSituation, searchTerm]);

  const sortedData = useMemo(() => {
    const list = [...filteredData];
    if (!sortConfig.key || !sortConfig.direction) return list;

    const parseDateTs = (v: string | null | undefined): number | null => {
      if (!v || String(v).trim() === "") return null;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? null : t;
    };

    const getValue = (item: AnalysisRow): string | number | null => {
      switch (sortConfig.key) {
        case "cnpj": {
          const d = onlyDigits(item.cnpj);
          return d || null;
        }
        case "sigla":
          return item.sigla ? item.sigla.toLowerCase() : null;
        case "solicitacao":
          return item.solicitacao || null;
        case "situacao":
          return item.situacao || null;
        case "envioDocumentacao":
          return parseDateTs(item.envioDocumentacao);
        case "ultimaAtualizacao":
        case "atualizacao":
          return parseDateTs(item.ultimaAtualizacao);
        case "solicitacaoAdequacao":
          return parseDateTs(item.solicitacaoAdequacao);
        case "adequacaoDocumentacao":
          return parseDateTs(item.adequacaoDocumentacao);
        case "conclusaoAnalise":
          return parseDateTs(item.conclusaoAnalise);
        default:
          return null;
      }
    };

    return list.sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key: string) => {
    let direction: string | null = "asc";
    let newKey: string | null = key;
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      newKey = null;
    }
    setSortConfig({ key: newKey, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "↕";
  };

  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);

  const handleOpenHistory = async (item: AnalysisRow) => {
    if (!item?.historicoHabilitado || !item?.idQuestionarioResposta) {
      if (ANALYSIS_FORCE_MOCK) {
        setSelectedHistory([
          {
            id: 1,
            acao: "Solicitação criada (mock)",
            data: formatHistoricoDocumentacaoDataHora(
              item.ultimaAtualizacao ?? new Date().toISOString(),
            ),
          },
          {
            id: 2,
            acao: "Documentação enviada (mock)",
            data: formatHistoricoDocumentacaoDataHora(
              item.envioDocumentacao ?? new Date().toISOString(),
            ),
          },
        ]);
        setShowHistoryModal(true);
      }
      return;
    }

    setSelectedHistory([]);
    setShowHistoryModal(true);
    setLoadingHistory(true);

    try {
      if (item.ano === 2025) {
        const response = await getHistoricoAcaoQuestionario(
          item.idQuestionarioResposta,
        );
        const historico = response?.data?.historicoAcaoQuestionario || [];
        setSelectedHistory(
          historico.map((entry: any) => ({
            acao: entry.nomeAcaoQuestionario || "-",
            data: formatHistoricoDocumentacaoDataHora(entry.dataHora),
          })),
        );
      } else {
        const response = await getEventosSolicitacaoAvaliacao(
          item.idQuestionarioResposta,
        );
        const eventosRaw = response?.data || response || [];
        const eventos = Array.isArray(eventosRaw) ? eventosRaw : [eventosRaw];
        setSelectedHistory(
          eventos.map((entry: any) => ({
            id: entry?.id,
            acao: entry?.nome || "-",
            data: formatHistoricoDocumentacaoDataHora(entry?.criadoEm),
          })),
        );
      }
    } catch (error) {
      console.error("❌ Erro ao carregar histórico:", error);
      setSelectedHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setSelectedHistory([]);
    setLoadingHistory(false);
  };

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      setCurrentPage(1);
      return;
    }

    setSelectedSituation((prev) => {
      if (prev.includes(value)) {
        return prev.filter((p) => p !== value);
      }

      return [...prev, value];
    });

    setCurrentPage(1);
  };

  const exportData = useMemo(
    () =>
      sortedData.map((item) => ({
        CNPJ: item.cnpj ?? "",
        SIGLA: item.sigla ?? "",
        SOLICITACAO: item.solicitacao ?? "",
        SITUACAO: item.situacao ?? "",
        DATA_ENVIO: formatDateTime(item.envioDocumentacao),
        DATA_ATUALIZACAO: formatDateTime(item.ultimaAtualizacao),
        SOLICITACAO_ADEQUACAO: formatDateTime(item.solicitacaoAdequacao),
        ADEQUACAO: formatDateTime(item.adequacaoDocumentacao),
        CONCLUSAO_ANALISE: formatDateTime(item.conclusaoAnalise),
      })),
    [sortedData],
  );

  const handleView = (item: AnalysisRow) => {
    const idResposta = item.idQuestionarioResposta;
    const idSindicato = item.idSindicato;
    const stateAnalise = {
      situacao: item.situacao ?? null,
      conclusaoAnalise:
        item.conclusaoAnalise ?? (item as { dataConclusaoAnalise?: string | null }).dataConclusaoAnalise ?? null,
    };

    if (Number(item.ano) > 2025) {
      if (!idResposta) return;
      navigate(
        PATHS.DOC_ANALYSIS_SOLICITACAO.replace(
          ":idSolicitacao",
          String(idResposta),
        ),
        { state: stateAnalise },
      );
      return;
    }

    if (!idResposta || !idSindicato) return;

    navigate(
      PATHS.ANALISE_DETALHE
        .replace(":idResposta", String(idResposta))
        .replace(":idSindicato", String(idSindicato)),
      { state: stateAnalise },
    );
  };

  if (initialLoading || loading) return <PageLoading />;

  return (
    <Container fluid className="analysis-container">
      <h1 className="analysis-title">LISTA DE SINDICATOS</h1>

      <>
        <div className="filters-actions-container shipping-filters-actions-container">
          <FilterSection
            selectedSituation={selectedSituation}
            handleSituationFilter={handleSituationFilter}
            allSituations={ALL_SITUATIONS}
          />

          <SearchExport
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            availableYears={availableYears}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            exportData={exportData}
            fileName={`ListaAnaliseSindicatos_${selectedYear}`}
          />
        </div>

        <ResultTable
          currentItems={currentItems}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          onOpenHistory={handleOpenHistory}
          onView={handleView}
          formatDate={formatDateTime}
          analisePorSolicitacaoAvaliacao={Number(selectedYear) > 2025}
        />

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />

        <DocumentationHistory
          show={showHistoryModal}
          onHide={handleCloseHistory}
          historyData={selectedHistory}
          loading={loadingHistory}
        />
      </>
    </Container>
  );
};

export default Analysis;
